import React, { FunctionComponent, useEffect } from 'react';
import { CheckCircleIcon } from 'lucide-react';
import { Button, Card, Heading, QueryLoaderWrapper, Text } from '@companion-professional/components';
import { CheckupRequestInfo } from '@companion-professional/dctypes';
import { NeedHelpContactSupport } from '../../components/NeedHelpContactSupport';
import { trackEvent } from '../../lib/trackEvent';
import { useCreateCheckupFromExistingCheckup } from '../../hooks/useCreateCheckupFromExistingCheckup';

interface CheckupCompleteProps {
  checkupRequest?: CheckupRequestInfo | null;
  message?: string;
}

// CheckupComplete is the page that is shown to the Pet Parent when they have completed the checkup.
export const CheckupComplete: FunctionComponent<CheckupCompleteProps> = ({
  checkupRequest,
  message = 'You can now close this tab. The results will be sent to your veterinarian.'
}) => {
  const { isPending, error, createCheckup } = useCreateCheckupFromExistingCheckup();

  useEffect(() => {
    trackEvent('checkup_complete_page_viewed', {
      completed_checkup_id: checkupRequest?.id
    });
  }, []);

  return (
    <div className="mb-4 flex flex-1 flex-col justify-between">
      <div className="flex flex-1 flex-col justify-center">
        <Card className="m-4 max-w-lg justify-start space-y-4 p-6 text-center">
          <div className="flex flex-row items-center justify-center gap-2">
            <CheckCircleIcon className="h-8 w-8 shrink-0 text-primary" />
            <Heading level="page">Checkup Complete!</Heading>
          </div>
          <Text leading="relaxed">{message}</Text>
          <NeedHelpContactSupport />
        </Card>
      </div>
      {checkupRequest ? (
        <div className="flex flex-row justify-center">
          <QueryLoaderWrapper isPending={isPending} error={error} size="small">
            <Button
              variant="secondary"
              type="button"
              onClick={async () => {
                trackEvent('create_another_checkup_clicked');
                const newCheckupId = await createCheckup(checkupRequest.id);
                if (newCheckupId) {
                  // Do a single full-page navigation to the new checkup.
                  window.location.replace(`/${newCheckupId}`);
                }
              }}
            >
              Upload another video
            </Button>
          </QueryLoaderWrapper>
        </div>
      ) : null}
    </div>
  );
};
